// EmailParsePanel — dialog showing the source's inbound email address with
// copy + sample-format hints. Replaces the "copy to clipboard via toast"
// shortcut that hid the per-source address behind one click. Operators
// who want to share the address with a partner now have a stable surface
// they can screenshot or paste from.
//
// Backend: the address is already provisioned at source create
// (services/app/src/routes/sources.ts:buildInputs → kind='email'). The
// ingestion handler that turns inbound mail into events is on the
// roadmap; this dialog is honest about that under "Status" so operators
// don't expect events from sending mail today.
//
// BACKLOG: Inputs Rail · Email parse panel.

import React, { useState } from 'react';
import type { Source } from '@datahub/shared';
import { useToast } from '../Toast';

export function EmailParsePanel({ source, onClose }: { source: Source; onClose: () => void }): React.ReactElement {
  const toast = useToast();
  const emailInput = source.inputs.find(i => i.kind === 'email');
  const address = emailInput?.address ?? '';
  const status = emailInput?.status ?? 'unknown';
  const [copied, setCopied] = useState(false);

  async function copyAddress(): Promise<void> {
    if (!address) return;
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
      toast.show('Address copied.', { kind: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.show('Clipboard write failed — copy manually.', { kind: 'error' });
    }
  }

  // Click-outside dismiss + Escape.
  React.useEffect(() => {
    function onKey(e: KeyboardEvent): void { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'oklch(0.18 0.01 250 / 0.4)', display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 80 }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-label="Email parse" style={{ width: 'min(560px, calc(100vw - 48px))', background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 12, padding: 24, maxHeight: 'calc(100vh - 120px)', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
          <span style={{ fontSize: 16, fontWeight: 600 }}>Email parse</span>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '2px 6px', borderRadius: 3, color: status === 'active' ? 'oklch(0.42 0.12 150)' : 'var(--ink-3)', background: status === 'active' ? 'oklch(0.92 0.06 150)' : 'var(--bg)', border: `1px solid ${status === 'active' ? 'oklch(0.78 0.10 150)' : 'var(--line)'}` }}>{status}</span>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginBottom: 16, lineHeight: 1.5 }}>
          Forward mail to this address from your inbox or a transactional sender. Each message becomes one event; attachments land in raw uploads on the same source.
        </div>

        {address ? (
          <>
            <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', marginBottom: 6 }}>Inbound address</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 6, marginBottom: 16 }}>
              <code style={{ flex: 1, fontFamily: 'var(--mono)', fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{address}</code>
              <button
                type="button"
                className="btn sm"
                onClick={() => { void copyAddress(); }}
                title="Copy address to clipboard"
              >{copied ? 'copied ✓' : 'copy'}</button>
            </div>
          </>
        ) : (
          <div style={{ padding: 12, background: 'var(--bg-sunken)', border: '1px dashed var(--line)', borderRadius: 6, marginBottom: 16, fontSize: 12, color: 'var(--ink-3)' }}>
            No email address provisioned for this source. (Reach out — this should always be present on a freshly-created source.)
          </div>
        )}

        <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', marginBottom: 6 }}>How partners use it</div>
        <ol style={{ margin: '0 0 16px 18px', padding: 0, fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.6 }}>
          <li>Add the address as a recipient (or auto-forward from a shared inbox).</li>
          <li>The mail body becomes the event payload. Attachments are stored as raw uploads on the source.</li>
          <li>Apply triggers (validate, enrich, forward, promote) the same way as webhook events.</li>
        </ol>

        <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', marginBottom: 6 }}>Status</div>
        <div style={{ padding: 10, background: 'oklch(0.97 0.03 85)', border: '1px solid oklch(0.86 0.08 85)', borderRadius: 6, fontSize: 12, color: 'oklch(0.42 0.10 60)', lineHeight: 1.5, marginBottom: 16 }}>
          <strong>Roadmap:</strong> the address is reserved on every Source; the inbound MX handler that converts mail → event is being wired up. Sending mail today won't yet produce events. Track progress under "Inputs Rail · Email parse panel" in BACKLOG.md.
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
